import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  ArrowLeftRight,
  CheckCircle,
  Building2,
  Users,
  Wallet,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/components/ui/tooltip'
import { Avatar, AvatarFallback, getInitials } from '@/components/ui/avatar'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/lib/api/auth'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@portal/types'

// ─── Nav structure ────────────────────────────────────────────────────────────

const MAIN_NAV = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'DASHBOARD' },
  { href: '/payments',  label: 'Payments',  icon: ArrowLeftRight,  module: 'PAYMENTS' },
  { href: '/approvals', label: 'Approvals', icon: CheckCircle,     module: 'APPROVALS', badge: true },
  { href: '/vendors',   label: 'Vendors',   icon: Building2,       module: 'VENDORS' },
  { href: '/users',     label: 'Users',     icon: Users,           module: 'USERS' },
]

const SECONDARY_NAV = [
  { href: '/account',  label: 'Account',  icon: Wallet },
  { href: '/settings', label: 'Settings', icon: Settings },
]

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
  USER: 'User',
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const user      = useAuthStore((s) => s.user)
  const modules   = useAuthStore((s) => s.modules)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const navigate  = useNavigate()

  const { data: pendingCount } = useQuery({
    queryKey: ['approvals-count'],
    queryFn: () =>
      apiClient.get('/api/approvals/pending/count') as unknown as Promise<ApiResponse<{ count: number }>>,
    select: (res) => res.data?.count ?? 0,
    refetchInterval: 60_000,
  })

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('portal_refresh_token') ?? ''
    try { await authApi.logout(refreshToken) } catch { /* ignore */ }
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-white/[0.07] bg-[#0B2545]',
        'transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-[64px]' : 'w-[224px]',
      )}
    >
      {/* ── Brand ─────────────────────────────────────────────────────────── */}
      <div
        className={cn(
          'flex h-[60px] shrink-0 items-center border-b border-white/[0.07]',
          collapsed ? 'justify-center px-0' : 'justify-between px-4',
        )}
      >
        {/* Logo */}
        <div className="flex items-center overflow-hidden">
          {collapsed ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/[0.12]">
              <img src="/icon.png" alt="Monarch" className="h-5 w-5 object-contain" />
            </div>
          ) : (
            <img src="/logo.png" alt="Monarch Innovation" className="h-7 w-auto object-contain" />
          )}
        </div>

        {/* Collapse toggle */}
        {!collapsed && (
          <button
            type="button"
            onClick={onToggle}
            aria-label="Collapse sidebar"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-white/10 hover:text-white/80"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Expand toggle (collapsed state — shown below brand icon) */}
      {collapsed && (
        <div className="flex justify-center py-2 border-b border-white/[0.07]">
          <button
            type="button"
            onClick={onToggle}
            aria-label="Expand sidebar"
            className="flex h-7 w-7 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-white/10 hover:text-white/80"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Navigation ────────────────────────────────────────────────────── */}
      <nav className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-2 py-3">
        {/* Main nav — filtered by module access */}
        <div className="flex flex-col gap-0.5">
          {MAIN_NAV.filter(({ module }) => modules.includes(module)).map(({ href, label, icon: Icon, badge }) => (
            <NavItem
              key={href}
              href={href}
              label={label}
              icon={<Icon className="shrink-0" size={17} />}
              collapsed={collapsed}
              badge={badge && (pendingCount ?? 0) > 0 ? String(pendingCount) : undefined}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="my-3 mx-1 h-px bg-white/[0.07]" />

        {/* Secondary nav */}
        <div className="flex flex-col gap-0.5">
          {SECONDARY_NAV.map(({ href, label, icon: Icon }) => (
            <NavItem
              key={href}
              href={href}
              label={label}
              icon={<Icon className="shrink-0" size={17} />}
              collapsed={collapsed}
            />
          ))}
        </div>
      </nav>

      {/* ── User area ─────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-t border-white/[0.07] px-2 py-3 space-y-0.5">
        {/* User profile row */}
        {user && (
          <Tooltip content={collapsed ? `${user.name}\n${ROLE_LABELS[user.role] ?? user.role}` : null} side="right">
            <div
              className={cn(
                'flex items-center rounded-lg px-2 py-2',
                collapsed && 'justify-center',
              )}
            >
              <Avatar size="sm" className="shrink-0">
                <AvatarFallback className="bg-white/[0.15] text-white text-[11px] font-semibold">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="ml-2.5 min-w-0 flex-1 overflow-hidden">
                  <p className="truncate text-[13px] font-medium leading-tight text-white">
                    {user.name}
                  </p>
                  <p className="mt-[2px] truncate text-[11px] leading-tight text-white/40">
                    {ROLE_LABELS[user.role] ?? user.role}
                  </p>
                </div>
              )}
            </div>
          </Tooltip>
        )}

        {/* Sign out */}
        <Tooltip content={collapsed ? 'Sign out' : null} side="right">
          <button
            type="button"
            onClick={handleLogout}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium',
              'text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/80',
              collapsed && 'justify-center',
            )}
          >
            <LogOut size={15} className="shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
        </Tooltip>
      </div>
    </aside>
  )
}

// ─── NavItem ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  href: string
  label: string
  icon: React.ReactNode
  collapsed: boolean
  badge?: string
}

function NavItem({ href, label, icon, collapsed, badge }: NavItemProps) {
  return (
    <Tooltip content={collapsed ? label : null} side="right">
      <NavLink
        to={href}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center rounded-lg text-[13px] font-medium transition-colors',
            'gap-2.5 px-2 py-[8.5px]',
            collapsed && 'justify-center',
            isActive
              ? 'bg-white/[0.14] text-white'
              : 'text-white/55 hover:bg-white/[0.07] hover:text-white/85',
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Left accent bar for active item */}
            {isActive && (
              <span className="absolute inset-y-[6px] left-0 w-[3px] rounded-r-full bg-white/90" />
            )}

            {icon}

            {!collapsed && (
              <span className="flex-1 truncate">{label}</span>
            )}

            {/* Approval badge */}
            {badge && (
              <span
                className={cn(
                  'flex h-[18px] min-w-[18px] items-center justify-center rounded-full',
                  'bg-amber-400 px-1 text-[10px] font-bold leading-none text-amber-950',
                  collapsed
                    ? 'absolute -right-0.5 -top-0.5 h-[15px] min-w-[15px] text-[9px]'
                    : 'ml-auto',
                )}
              >
                {badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </Tooltip>
  )
}
